import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { operationSession } from "../../../../../../shared/frontend/operations/operationSession";
import { useOperations } from "../../../../../../shared/frontend/operations/useOperations";
import { useAuth } from "../auth/useAuth";
import { refreshFinanceData } from "./financeRefresh";
export { refreshFinanceData } from "./financeRefresh";
import { ApiError } from "../api/client";
import { HTTP_STATUS_REQUEST_TIMEOUT } from "../constants/httpStatus";
import { HTTP_SERVER_ERROR_MIN } from "../api/httpStatusRanges";

export const personFinanceResource = (personId: string) => JSON.stringify(["person-finance", personId]);
export const bankStatementResource = (bankAccountId: string, key: string) => JSON.stringify(["bank-statement", bankAccountId, key]);

/** QueryClient owns the lifetime; API instance and auth scope isolate sessions without storing tokens. */
export function useFinanceOperations() {
  const client = useQueryClient();
  const auth = useAuth();
  const api = auth.api;
  const scope = JSON.stringify([auth.info?.tenantId, auth.info?.accountId]);
  const store = operationSession(client, scope, api);
  const entries = useOperations(store);
  const forResource = useCallback((resource: string) => entries.find(entry => entry.resources.includes(resource)), [entries]);
  const personActionProps = useCallback((personId: string) => {
    const entry = forResource(personFinanceResource(personId));
    return { loading: Boolean(entry && entry.phase !== "refreshFailed"), disabled: Boolean(entry) };
  }, [forResource]);
  return { store, entries, forResource, personActionProps, client };
}

export function useFinanceOperation(personId: string, extraResources: readonly string[] = []) {
  const { store, forResource, client } = useFinanceOperations();
  const resource = personFinanceResource(personId);
  const entry = forResource(resource) ?? extraResources.map(forResource).find(entry => entry !== undefined);
  const execute = useCallback((operation: (committed: () => void) => Promise<void>, refresh?: () => Promise<void>) => store.execute({
    resources: [resource, ...extraResources], operation,
    // fetch rejects transport failures with TypeError; ApiClient also treats these as network failures.
    isUncertainWriteError: error => error instanceof TypeError || (error instanceof ApiError
      && (error.status === 0 || error.status === HTTP_STATUS_REQUEST_TIMEOUT || error.status >= HTTP_SERVER_ERROR_MIN)),
    refresh: async () => { if (refresh) await refresh(); await refreshFinanceData(client, personId); },
  }), [store, resource, extraResources, client, personId]);
  return { execute, entry, pending: Boolean(entry && entry.phase !== "refreshFailed"), retry: () => { if (entry) void store.retry(entry.id); } };
}
